import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Fab from '@mui/material/Fab';
import Tooltip from '@mui/material/Tooltip';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { EASE } from './ui';
import styles from './ScreenInspector.module.css';

const SLIDE_MS = 650;
// Wheel events closer together than this belong to the same swipe (trackpad inertia).
const GESTURE_GAP_MS = 220;

const variants = {
    enter: (dir) => ({ y: dir > 0 ? '14%' : '-14%', opacity: 0 }),
    center: { y: '0%', opacity: 1 },
    exit: (dir) => ({ y: dir > 0 ? '-14%' : '14%', opacity: 0 }),
};

const FAB_SX = {
    width: 36,
    height: 36,
    minHeight: 36,
    borderRadius: '6px',
    bgcolor: '#fff',
    color: '#1d1d1f',
    boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
    '&:hover': { bgcolor: '#f5f5f7' },
    '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.7)', color: '#c7c7cc', boxShadow: 'none' },
};

// True when the wheel should scroll something inside the slide instead of turning the page.
function canScrollInside(target, root, deltaY) {
    for (let el = target; el && el !== root; el = el.parentElement) {
        const { overflowY } = getComputedStyle(el);
        if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight + 1) {
            if (deltaY > 0 && el.scrollTop + el.clientHeight < el.scrollHeight - 1) return true;
            if (deltaY < 0 && el.scrollTop > 0) return true;
        }
    }
    return false;
}

function isInteractive(el) {
    return el && el.closest && el.closest('button, a, input, textarea, select, [role="tab"]');
}

/**
 * Full-screen slides with side up/down buttons, like a presentation.
 * Each slide is { id, label, tone, builds, render({ build, setBuild, next }) };
 * a slide with builds > 1 steps through them before moving on.
 */
export default function Deck({ slides, prevLabel, nextLabel, corner }) {
    const [pos, setPos] = useState({ i: 0, b: 0, dir: 1 });
    const [height, setHeight] = useState(null);
    const rootRef = useRef(null);
    const busyUntil = useRef(0);
    const lastWheel = useRef(0);

    // Fill the window below the site's navigation bar.
    useLayoutEffect(() => {
        const fit = () => {
            const el = rootRef.current;
            if (!el) return;
            const top = el.getBoundingClientRect().top + window.scrollY;
            setHeight(Math.max(480, window.innerHeight - top));
        };
        window.scrollTo(0, 0);
        fit();
        window.addEventListener('resize', fit);
        return () => window.removeEventListener('resize', fit);
    }, []);

    const go = useCallback((delta) => {
        setPos((p) => {
            const builds = slides[p.i].builds || 1;
            if (delta > 0) {
                if (p.b < builds - 1) return { ...p, b: p.b + 1 };
                if (p.i < slides.length - 1) return { i: p.i + 1, b: 0, dir: 1 };
            } else {
                if (p.b > 0) return { ...p, b: p.b - 1 };
                if (p.i > 0) return { i: p.i - 1, b: (slides[p.i - 1].builds || 1) - 1, dir: -1 };
            }
            return p;
        });
    }, [slides]);

    const goTo = useCallback((i) => {
        setPos((p) => (p.i === i ? { ...p, b: 0 } : { i, b: 0, dir: i > p.i ? 1 : -1 }));
    }, []);

    const setBuild = useCallback((b) => setPos((p) => ({ ...p, b })), []);

    // Keyboard: arrows, Page Up / Down, space, Home / End.
    useEffect(() => {
        const onKey = (e) => {
            if (e.altKey || e.ctrlKey || e.metaKey) return;
            let delta = 0;
            if (['ArrowDown', 'PageDown'].includes(e.key)) delta = 1;
            else if (['ArrowUp', 'PageUp'].includes(e.key)) delta = -1;
            else if (e.key === ' ' && !isInteractive(e.target)) delta = e.shiftKey ? -1 : 1;
            else if (e.key === 'Home') { e.preventDefault(); goTo(0); return; }
            else if (e.key === 'End') { e.preventDefault(); goTo(slides.length - 1); return; }
            if (!delta) return;
            e.preventDefault();
            go(delta);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [go, goTo, slides.length]);

    // Mouse wheel / trackpad: one swipe turns one page.
    useEffect(() => {
        const el = rootRef.current;
        if (!el) return undefined;
        const onWheel = (e) => {
            const now = Date.now();
            const gap = now - lastWheel.current;
            lastWheel.current = now;
            if (canScrollInside(e.target, el, e.deltaY)) return;
            e.preventDefault();
            if (gap < GESTURE_GAP_MS || now < busyUntil.current || Math.abs(e.deltaY) < 4) return;
            busyUntil.current = now + SLIDE_MS;
            go(e.deltaY > 0 ? 1 : -1);
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [go]);

    const slide = slides[pos.i];
    const atStart = pos.i === 0 && pos.b === 0;
    const atEnd = pos.i === slides.length - 1 && pos.b === (slide.builds || 1) - 1;

    return (
        <div
            ref={rootRef}
            className={styles.deck}
            style={height ? { height, '--deck-h': `${height}px` } : undefined}
        >
            <AnimatePresence initial={false} custom={pos.dir}>
                <motion.section
                    key={slide.id}
                    className={`${styles.slide} ${slide.tone ? styles[slide.tone] : ''}`}
                    custom={pos.dir}
                    variants={variants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: SLIDE_MS / 1000, ease: EASE }}
                    aria-label={slide.label}
                >
                    <div className={styles.slideInner}>
                        {slide.render({ build: pos.b, setBuild, next: () => go(1) })}
                    </div>
                </motion.section>
            </AnimatePresence>

            {corner && <div className={styles.deckCorner}>{corner}</div>}

            <nav className={styles.deckNav} aria-label="Slides">
                <Tooltip title={prevLabel} placement="left">
                    <span>
                        <Fab size="small" sx={FAB_SX} onClick={() => go(-1)} disabled={atStart} aria-label={prevLabel}>
                            <KeyboardArrowUpRoundedIcon />
                        </Fab>
                    </span>
                </Tooltip>
                <div className={styles.deckDots}>
                    {slides.map((s, i) => (
                        <Tooltip key={s.id} title={s.label} placement="left">
                            <button
                                type="button"
                                className={`${styles.deckDot} ${i === pos.i ? styles.deckDotOn : ''}`}
                                onClick={() => goTo(i)}
                                aria-label={s.label}
                                aria-current={i === pos.i ? 'step' : undefined}
                            />
                        </Tooltip>
                    ))}
                </div>
                <Tooltip title={nextLabel} placement="left">
                    <span>
                        <Fab size="small" sx={FAB_SX} onClick={() => go(1)} disabled={atEnd} aria-label={nextLabel}>
                            <KeyboardArrowDownRoundedIcon />
                        </Fab>
                    </span>
                </Tooltip>
                <span className={styles.deckCount}>{pos.i + 1} / {slides.length}</span>
            </nav>
        </div>
    );
}
